import type { DocFieldSchema } from "@/lib/erp/meta";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { ChildTableField } from "@/components/child-table";
import { AttachmentField } from "@/components/fields/AttachmentField";
import { cn } from "@/lib/utils";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";

/**
 * Parse Frappe's native `link_filters` DocField property into the shape
 * `LinkFieldSearch.defaultFilters` expects.
 *
 * Frappe stores it as a JSON string in two possible shapes:
 *   - Dict form:        '{"has_variants": 0}'
 *   - Nested-array form: '[["Item","has_variants","=",0], ...]'
 *
 * Only `=` operator entries from the array form map cleanly to a flat
 * key→value dict; other operators are ignored at the picker level.
 */
function parseLinkFilters(raw: string | undefined): Record<string, any> | undefined {
  if (!raw || typeof raw !== "string") return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.keys(parsed).length > 0 ? (parsed as Record<string, any>) : undefined;
    }
    if (Array.isArray(parsed)) {
      const out: Record<string, any> = {};
      for (const entry of parsed) {
        if (Array.isArray(entry) && entry.length >= 4 && entry[2] === "=") {
          out[String(entry[1])] = entry[3];
        }
      }
      return Object.keys(out).length > 0 ? out : undefined;
    }
  } catch {
    // Malformed JSON — fall through to undefined
  }
  return undefined;
}

interface FieldRendererProps {
  field: DocFieldSchema;
  value: any;
  onChange: (value: any) => void;
  onBlur?: (value: any) => void; // Optional onBlur callback (used for Link field validation)
  disabled?: boolean; // base disabled state
  isReadOnly?: boolean; // computed from dependencies
  isRequired?: boolean; // computed from dependencies
  resetKey?: string | number; // Reset key for child table resync
  viewMode?: 'system' | 'experience'; // Controls child table column filtering
  documentName?: string; // Document identifier (name/id) - required for attachment fields
  parentDoc?: Record<string, any>; // Parent document data for child table server fetch context
}

/**
 * Dynamic field renderer based on fieldtype
 * 
 * Renders appropriate input component based on ERPNext field type.
 * Follows meta-driven approach - no hardcoded Item fields.
 */
export function FieldRenderer({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  isReadOnly: computedReadOnly,
  isRequired: computedRequired,
  resetKey,
  viewMode,
  documentName,
  parentDoc,
}: FieldRendererProps) {
  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
  const fieldLabel = resolvedLabel.label;

  // Skip hidden fields (should already be filtered, but safety check)
  if (field.hidden === 1) {
    return null;
  }

  // Compute final states (computed props override schema)
  const required = computedRequired ?? field.reqd === 1;
  const readOnly = computedReadOnly ?? field.read_only === 1;
  const isDisabled = disabled || readOnly;

  // Render field based on fieldtype
  switch (field.fieldtype) {
    case "Link": {
      // Schema-driven filter: honor Frappe's native `link_filters` DocField property.
      // No hardcoded fallback — the DocType schema decides what's selectable, not the UI.
      const linkFilters = parseLinkFilters(field.link_filters);
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <LinkFieldSearch
            doctype={field.options || ""}
            value={value || null}
            onChange={onChange}
            onBlur={onBlur ? () => onBlur(value) : undefined} // Pass onBlur with current value
            disabled={isDisabled}
            placeholder={`Select ${fieldLabel}`}
            className="w-full"
            data-field={field.fieldname}
            defaultFilters={linkFilters}
          />
        </div>
      );
    }

    case "Select":
      const options = field.options?.split("\n").filter(Boolean) || [];
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Select
            value={value || ""}
            onValueChange={onChange}
            disabled={isDisabled}
          >
            <SelectTrigger 
              id={field.fieldname} 
              className="w-full h-11 rounded bg-gray-50 dark:bg-gray-900/50 border-0 px-4 text-[17px] font-normal text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors" 
              data-field={field.fieldname}
            >
              <SelectValue placeholder={`Select ${fieldLabel}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case "Check":
      return (
        <div className="flex items-center justify-between" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn("font-normal", required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Switch
            id={field.fieldname}
            data-field={field.fieldname}
            checked={value === 1 || value === true}
            onCheckedChange={(checked) => onChange(checked ? 1 : 0)}
            disabled={isDisabled}
          />
        </div>
      );

    case "Int":
    case "Float":
    case "Currency":
    case "Percent":
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Input
            id={field.fieldname}
            type="number"
            data-field={field.fieldname}
            value={value ?? ""}
            onChange={(e) => {
              const numValue = e.target.value === "" ? null : Number(e.target.value);
              onChange(numValue);
            }}
            disabled={isDisabled}
            placeholder={`Enter ${fieldLabel}`}
          />
        </div>
      );

    case "Date":
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <DatePicker
            value={value || undefined}
            onChange={(dateStr) => onChange(dateStr || null)}
            disabled={isDisabled}
            placeholder={`Select ${fieldLabel}`}
            className="w-full"
            data-field={field.fieldname}
          />
        </div>
      );

    case "Datetime":
      // For now, treat as Date - can enhance later
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Input
            id={field.fieldname}
            type="datetime-local"
            data-field={field.fieldname}
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={isDisabled}
            placeholder={`Select ${fieldLabel}`}
          />
        </div>
      );

    case "Text":
    case "Small Text":
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Input
            id={field.fieldname}
            type="text"
            data-field={field.fieldname}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
            placeholder={`Enter ${fieldLabel}`}
          />
        </div>
      );

    case "Long Text":
    case "Text Editor":
    case "Code":
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Textarea
            id={field.fieldname}
            data-field={field.fieldname}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
            placeholder={`Enter ${fieldLabel}`}
            rows={4}
          />
        </div>
      );

    case "Table":
      return (
        <ChildTableField
          field={field}
          value={value || []}
          onChange={onChange}
          disabled={isDisabled}
          resetKey={resetKey}
          viewMode={viewMode}
          parentDoctype={doctype}
          parentDoc={parentDoc}
        />
      );

    case "Attach":
      return (
        <AttachmentField
          field={field}
          value={value ?? null}
          onChange={onChange}
          disabled={isDisabled}
          isImage={false}
          doctype={doctype}
          name={documentName || ""}
        />
      );

    case "Attach Image":
      return (
        <AttachmentField
          field={field}
          value={value ?? null}
          onChange={onChange}
          disabled={isDisabled}
          isImage={true}
          doctype={doctype}
          name={documentName || ""}
        />
      );

    case "Data":
    default:
      // Default to text input for Data and unknown types
      return (
        <div className="space-y-3" data-field={field.fieldname}>
          <Label htmlFor={field.fieldname} className={cn(required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
            {fieldLabel}
          </Label>
          <Input
            id={field.fieldname}
            type="text"
            data-field={field.fieldname}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
            placeholder={`Enter ${fieldLabel}`}
          />
        </div>
      );
  }
}

